import React, { useState, useEffect, useMemo } from "react";
import { useMaintenance } from "../../context/MaintenanceContext";
import { motion, AnimatePresence } from "framer-motion";

const formatDateTime = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export default function MaintenanceBanner() {
  const {
    isScheduled,
    maintenanceInfo,
    isBannerDismissed,
    dismissBanner,
    isUnderMaintenance,
    checkMaintenanceStatus,
  } = useMaintenance();

  const [timeLeftStr, setTimeLeftStr] = useState("");
  const [showReasonModal, setShowReasonModal] = useState(false);

  // If already under maintenance, the full maintenance page will handle it, so hide banner
  const shouldShow =
    isScheduled &&
    !isUnderMaintenance &&
    !isBannerDismissed &&
    maintenanceInfo?.startDateTime;

  // Realtime countdown until maintenance begins
  useEffect(() => {
    if (!shouldShow || !maintenanceInfo?.startDateTime) return;

    const updateCountdown = () => {
      const now = Date.now();
      const start = new Date(maintenanceInfo.startDateTime).getTime();
      const diff = start - now;

      if (diff <= 0) {
        setTimeLeftStr("Starting now");
        checkMaintenanceStatus();
        return;
      }

      if (diff < 60000) {
        setTimeLeftStr("Starts in < 1m");
        return;
      }

      const totalMinutes = Math.ceil(diff / (1000 * 60));
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      const days = Math.floor(hours / 24);

      if (days > 0) {
        setTimeLeftStr(`Starts in ${days}d ${hours % 24}h`);
      } else if (hours > 0) {
        setTimeLeftStr(
          `Starts in ${hours}h ${minutes > 0 ? `${minutes}m` : ""}`,
        );
      } else {
        setTimeLeftStr(`Starts in ${minutes}m`);
      }
    };

    updateCountdown();
    // Use faster 1s interval when within 2 minutes of start, otherwise 10s
    const start = new Date(maintenanceInfo.startDateTime).getTime();
    const isImminent = start - Date.now() < 120000;
    const interval = setInterval(updateCountdown, isImminent ? 1000 : 10000);

    return () => clearInterval(interval);
  }, [shouldShow, maintenanceInfo?.startDateTime, checkMaintenanceStatus]);

  const formattedStart = useMemo(
    () => formatDateTime(maintenanceInfo?.startDateTime),
    [maintenanceInfo?.startDateTime],
  );

  if (!shouldShow) return null;

  return (
    <>
      <div className="maintenance-scheduled-banner">
        <div className="maintenance-scheduled-banner-inner">
          <div className="maintenance-banner-left">
            <span className="maintenance-banner-badge">
              <i className="fa-solid fa-clock-rotate-left"></i>
              <span>Scheduled Maintenance</span>
            </span>

            <div className="maintenance-banner-text">
              <strong className="maintenance-banner-highlight">
                Starts: {formattedStart} (Est. ~{maintenanceInfo?.estimatedHours || 2} hrs)
              </strong>
              <span className="maintenance-banner-subtext">
                GradeMe-AI will be temporarily offline for planned updates.
              </span>
            </div>
          </div>

          <div className="maintenance-banner-right">
            {timeLeftStr && (
              <span
                className="maintenance-banner-countdown"
                title="Time until maintenance start"
              >
                <i className="fa-regular fa-hourglass-half"></i>
                {timeLeftStr}
              </span>
            )}

            {maintenanceInfo?.reason && (
              <button
                type="button"
                className="maintenance-banner-reason-btn"
                onClick={() => setShowReasonModal(true)}
                title="View details"
              >
                <i className="fa-solid fa-circle-info"></i>
                <span>Reason</span>
              </button>
            )}

            <button
              type="button"
              className="maintenance-banner-dismiss-btn"
              onClick={dismissBanner}
              aria-label="Dismiss maintenance notification"
              title="Dismiss notification"
            >
              <i className="fa-solid fa-xmark"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Detail Modal for Reason */}
      <AnimatePresence>
        {showReasonModal && (
          <div
            className="maintenance-reason-overlay"
            onClick={() => setShowReasonModal(false)}
          >
            <motion.div
              className="maintenance-reason-modal"
              onClick={(e) => e.stopPropagation()}
              initial={{ opacity: 0, scale: 0.95, y: 15 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 15 }}
              transition={{ duration: 0.2 }}
            >
              <div className="maintenance-reason-header">
                <div className="maintenance-reason-header-title">
                  <div className="maintenance-reason-icon-wrap">
                    <i className="fa-solid fa-screwdriver-wrench"></i>
                  </div>
                  <div>
                    <h4>
                      {maintenanceInfo?.title ||
                        "Scheduled Platform Maintenance"}
                    </h4>
                    <span className="maintenance-reason-window">
                      Starts: {formattedStart} · Estimated Duration: ~{maintenanceInfo?.estimatedHours || 2} hrs
                    </span>
                  </div>
                </div>
                <button
                  type="button"
                  className="maintenance-reason-close"
                  onClick={() => setShowReasonModal(false)}
                  aria-label="Close"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div className="maintenance-reason-body">
                <div className="maintenance-reason-section">
                  <label className="maintenance-reason-label">
                    <i className="fa-solid fa-comment-dots"></i> Reason for
                    Maintenance
                  </label>
                  <div className="maintenance-reason-content-box">
                    {maintenanceInfo?.reason}
                  </div>
                </div>

                <div className="maintenance-reason-notice">
                  <i className="fa-solid fa-circle-exclamation"></i>
                  <span>
                    During this window, assessment submissions and AI grading
                    will be temporarily paused. Your previous reports and
                    grading data remain 100% secure.
                  </span>
                </div>
              </div>

              <div className="maintenance-reason-footer">
                <button
                  type="button"
                  className="maintenance-reason-ok-btn"
                  onClick={() => setShowReasonModal(false)}
                >
                  Understood
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
}
